import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { FileText, Calendar, Plus, Trash2, X } from "lucide-react";
import {
  getProjectById,
  getTemplateById,
} from "../../api/projectManagementApi";
import { CheckpointsStepProps } from "./types";
import TiptapEditor from "@/components/ui/TiptapEditor";

export const ReviewCheckpointsStep = React.memo<CheckpointsStepProps>(
  ({
    setDataToMatch,
    projectData,
    selectedCheckpointId,
    setSelectedCheckpointId,
    addCheckpoint,
    temporaryProjectId,
    updateCheckpoint,
    setProjectData,
  }) => {
    const [searchParams] = useSearchParams();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const { projectId } = useParams();
    const startDateRef = React.useRef<HTMLInputElement>(null);
    const dueDateRef = React.useRef<HTMLInputElement>(null);
    const mode = searchParams.get("mode");
    const action = searchParams.get("action");
    const resolvedId = projectId || temporaryProjectId;

    const { data, isLoading } = useQuery({
      queryKey: ["project", resolvedId],
      queryFn: () => getProjectById(resolvedId || ""),
      enabled: !!resolvedId && mode !== "template",
    });

    const { data: templateData, isLoading: templateLoading } = useQuery({
      queryKey: ["template", resolvedId],
      queryFn: () => getTemplateById(resolvedId || ""),
      enabled: !!resolvedId && mode === "template" && action === "edit",
    });

    // Pre-fill template data in edit mode
    useEffect(() => {
      if (templateData && action === "edit" && mode === "template") {
        setProjectData((prev: any) => ({
          ...prev,
          ...templateData,
          title: (templateData as any).template_name || prev.title,
          description:
            (templateData as any).template_description || prev.description,
          estimatedHours:
            (templateData as any).estimated_hours || prev.estimatedHours,
        }));
      }
    }, [templateData, action, mode, setProjectData]);

    // Set matched data and select first checkpoint
    useEffect(() => {
      if (!data) return;
      setDataToMatch(data);
      if (data.checkpoints && data.checkpoints.length > 0) {
        setSelectedCheckpointId(data.checkpoints[0].checkpoint_number);
      }
    }, [data, setDataToMatch, setSelectedCheckpointId]);

    // Initialize project data from fetched data
    useEffect(() => {
      if (!data) return;
      if (projectData) return;
      setProjectData(data as any);
    }, [data, projectData, setProjectData]);

    const selectedCheckpoint =
      projectData?.checkpoints?.find(
        (cp: any) => cp.checkpoint_number === selectedCheckpointId,
      ) || projectData?.checkpoints?.[0];

    const deleteCheckpoint = (checkpointId: number) => {
      try {
        const updatedCheckpoints = projectData.checkpoints.filter(
          (checkpoint: any) => checkpoint.checkpoint_number !== checkpointId,
        );

        setProjectData((prev: any) => ({
          ...prev,
          checkpoints: updatedCheckpoints,
        }));

        if (selectedCheckpointId === checkpointId) {
          if (updatedCheckpoints.length > 0) {
            setSelectedCheckpointId(
              updatedCheckpoints[0]?.checkpoint_number || 0,
            );
          } else {
            setSelectedCheckpointId(0);
          }
        }

        toast.success("Checkpoint deleted successfully");
      } catch (error: any) {
        toast.error(
          error?.data?.data?.message || "Failed to delete checkpoint",
        );
      }
    };

    return (
      <div className="space-y-6">
        {isLoading || templateLoading ? (
          <Card className="h-[50vh]">
            <p className="flex justify-center items-center h-full">
              Fetching Project Details...
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Checkpoints Sidebar */}
            <Card className="h-fit">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-bits-blue" />
                  <CardTitle className="text-base">Checkpoints</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {projectData?.checkpoints?.map(
                  (checkpoint: any, index: number) => {
                    return (
                      <div
                        key={`checkpoint-${
                          checkpoint.checkpoint_number || index
                        }`}
                        onClick={() => {
                          setSelectedCheckpointId(checkpoint.checkpoint_number);
                        }}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          checkpoint.checkpoint_number === selectedCheckpointId
                            ? "bg-blue-50 border-blue-200"
                            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium">
                            {checkpoint.title}
                          </span>
                        </div>
                      </div>
                    );
                  },
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addCheckpoint}
                  className="justify-start text-bits-blue hover:text-blue-700 mt-3"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </CardContent>
            </Card>

            {/* Checkpoint Details - Main Content */}
            <div className="lg:col-span-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-bits-blue" />
                    <CardTitle className="text-lg">
                      Checkpoint Details
                    </CardTitle>
                  </div>
                  <p className="text-sm text-gray-600">
                    Configure the timeline and requirements for each project
                    milestone
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Delete Confirmation Dialog */}
                  {showDeleteDialog && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw] shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Delete Checkpoint
                          </h3>
                          <button
                            onClick={() => setShowDeleteDialog(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <X size={20} />
                          </button>
                        </div>

                        <p className="text-gray-600 mb-6">
                          Are you sure you want to delete this checkpoint?
                          Action cannot be undone
                        </p>

                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setShowDeleteDialog(false)}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              deleteCheckpoint(selectedCheckpointId);
                              setShowDeleteDialog(false);
                            }}
                            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {projectData?.checkpoints &&
                  projectData.checkpoints.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-2">
                        No checkpoints added yet
                      </p>
                      <p className="text-sm text-gray-400">
                        Click "Add" to add a checkpoint
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Checkpoint Name */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Checkpoint Name</Label>
                          <button
                            onClick={() => setShowDeleteDialog(true)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Delete checkpoint"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <Input
                          placeholder="Enter Checkpoint name"
                          value={selectedCheckpoint?.title || ""}
                          onChange={(e) =>
                            updateCheckpoint(
                              selectedCheckpointId,
                              "title",
                              e.target.value,
                            )
                          }
                          className="bg-bits-grey-100"
                        />
                      </div>
                      {/* Checkpoint Description */}
                      <div className="space-y-2">
                        <Label>Checkpoint Description</Label>
                        <TiptapEditor
                          id="description"
                          placeholder="Brief description of the project"
                          className="bg-bits-grey-100 text-bits-grey-600"
                          value={selectedCheckpoint?.description || ""}
                          onChange={(value) =>
                            updateCheckpoint(
                              selectedCheckpointId,
                              "description",
                              value,
                            )
                          }
                        />
                      </div>{" "}
                      {/* Start and Due Dates */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Start Date */}
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            ref={startDateRef}
                            className="bg-bits-grey-100 scheme-light [&::-webkit-datetime-edit-month-field]:text-gray-900 [&::-webkit-datetime-edit-day-field]:text-gray-900 [&::-webkit-datetime-edit-year-field]:text-gray-900"
                            type="date"
                            key={`start-${selectedCheckpointId}`}
                            value={(() => {
                              const startDate = selectedCheckpoint?.start_date;
                              if (
                                !startDate ||
                                typeof startDate !== "string" ||
                                startDate.trim() === ""
                              ) {
                                return "";
                              }
                              try {
                                const date = new Date(startDate);
                                if (isNaN(date.getTime())) {
                                  return "";
                                }
                                const year = date.getFullYear();
                                const month = String(
                                  date.getMonth() + 1,
                                ).padStart(2, "0");
                                const day = String(date.getDate()).padStart(
                                  2,
                                  "0",
                                );
                                return `${year}-${month}-${day}`;
                              } catch {
                                return "";
                              }
                            })()}
                            onChange={(e) => {
                              const newStart = e.target.value;
                              if (!newStart || newStart.trim() === "") {
                                updateCheckpoint(
                                  selectedCheckpointId,
                                  "start_date",
                                  "",
                                );
                                if (startDateRef.current) {
                                  startDateRef.current.value = "";
                                }
                                return;
                              }
                              updateCheckpoint(
                                selectedCheckpointId,
                                "start_date",
                                newStart,
                              );
                            }}
                          />
                        </div>

                        {/* Due Date */}
                        <div className="space-y-2">
                          <Label>Due Date</Label>
                          <Input
                            ref={dueDateRef}
                            className="bg-bits-grey-100 scheme-light [&::-webkit-datetime-edit-month-field]:text-gray-900 [&::-webkit-datetime-edit-day-field]:text-gray-900 [&::-webkit-datetime-edit-year-field]:text-gray-900"
                            type="date"
                            key={`due-${selectedCheckpointId}`}
                            value={(() => {
                              const dueDate = selectedCheckpoint?.due_date;
                              if (
                                !dueDate ||
                                typeof dueDate !== "string" ||
                                dueDate.trim() === ""
                              ) {
                                return "";
                              }
                              try {
                                const date = new Date(dueDate);
                                if (isNaN(date.getTime())) {
                                  return "";
                                }
                                const year = date.getFullYear();
                                const month = String(
                                  date.getMonth() + 1,
                                ).padStart(2, "0");
                                const day = String(date.getDate()).padStart(
                                  2,
                                  "0",
                                );
                                return `${year}-${month}-${day}`;
                              } catch {
                                return "";
                              }
                            })()}
                            onChange={(e) => {
                              const newDue = e.target.value;
                              if (!newDue || newDue.trim() === "") {
                                updateCheckpoint(
                                  selectedCheckpointId,
                                  "due_date",
                                  "",
                                );
                                if (dueDateRef.current) {
                                  dueDateRef.current.value = "";
                                }
                                return;
                              }
                              updateCheckpoint(
                                selectedCheckpointId,
                                "due_date",
                                newDue,
                              );
                            }}
                          />
                        </div>
                      </div>
                      {/* Mandatory Checkbox */}
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedCheckpoint?.is_required ?? false}
                            onChange={(e) =>
                              updateCheckpoint(
                                selectedCheckpointId,
                                "is_required",
                                e.target.checked,
                              )
                            }
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Mandatory to proceed</span>
                        </label>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    );
  },
);

ReviewCheckpointsStep.displayName = "ReviewCheckpointsStep";
